import { useMemo } from 'react';
import { Form } from 'react-final-form';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import goToNextHealthStep from 'utils/goToNextHealthStep';

import { HEADING } from './constants';
import SleepApneaForm from './form';
import { SleepApneaFormValues } from './form/types';
import { useSleepApneaProfileQuery } from './graphql/SleepApneaProfile';
import { useUpdateSleepApneaProfileMutation } from './graphql/UpdateSleepApneaProfile';
import buildInitialValues from './utils/buildInitialValues';
import buildInput from './utils/buildInput';
import { getValidator } from './utils/validate';

const SleepApnea = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.SleepApnea);
  const { data, loading } = useSleepApneaProfileQuery();
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const dateOfBirth = data?.currentUser.dateOfBirth;
  const validate = useMemo(() => getValidator({ dateOfBirth }), [dateOfBirth]);

  const [updateSleepApneaProfile] = useUpdateSleepApneaProfileMutation({
    onCompleted(res) {
      trackStepCompleted();
      goToNextHealthStep(res.updateSleepApnea?.nextStepPath);
    },
  });

  const onSubmit = (values: SleepApneaFormValues) =>
    updateSleepApneaProfile({
      variables: {
        input: buildInput(values),
      },
    });

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} title={Steps.SleepApnea}>
      <Form<SleepApneaFormValues, InitialValues<SleepApneaFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => <SleepApneaForm {...formProps} />}
      </Form>
    </NavigatorContent>
  );
};

export default SleepApnea;
